/**
 * Deep links into business WhatsApp / Telegram with optional context.
 * Client-side only — no server relay of chat content.
 */

import {
  COMMUNITY_ASK_INTENTS,
  COMMUNITY_TELEGRAM_URL,
  COMMUNITY_WHATSAPP_E164,
} from '../config/communityChannels';

/**
 * @param {string} [appId]
 * @param {string} [customText]
 */
export const resolveAskMessage = (appId, customText) => {
  if (customText && customText.trim()) {
    return customText.trim();
  }
  return COMMUNITY_ASK_INTENTS[appId] || COMMUNITY_ASK_INTENTS.general;
};

/**
 * @returns {string|null}
 */
export const buildWhatsAppAskUrl = ({ appId, text } = {}) => {
  if (!COMMUNITY_WHATSAPP_E164) {
    return null;
  }
  const digits = COMMUNITY_WHATSAPP_E164.replace(/\D/g, '');
  if (!digits) {
    return null;
  }
  const message = resolveAskMessage(appId, text);
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
};

/**
 * Telegram public chats/groups don’t support reliable prefill; open the community URL.
 * Optional ?start= for bots when URL is t.me/BotName.
 * @returns {string|null}
 */
export const buildTelegramAskUrl = ({ appId } = {}) => {
  if (!COMMUNITY_TELEGRAM_URL) {
    return null;
  }
  try {
    const url = new URL(COMMUNITY_TELEGRAM_URL);
    // Bot deep-link: append start payload with app context
    if (url.pathname.length > 1 && !url.pathname.includes('+') && !url.searchParams.has('start')) {
      const start = `ask_${appId || 'general'}`.replace(/[^a-z0-9_]/gi, '').slice(0, 64);
      if (start && !url.pathname.includes('/joinchat') && !url.pathname.includes('/+')) {
        // Only add start for likely bot usernames (single segment, no join path)
        const segments = url.pathname.split('/').filter(Boolean);
        if (segments.length === 1) {
          url.searchParams.set('start', start);
        }
      }
    }
    return url.toString();
  } catch {
    return COMMUNITY_TELEGRAM_URL;
  }
};

export const getHelpChannelLinks = ({ appId, text } = {}) => {
  const links = [];
  const wa = buildWhatsAppAskUrl({ appId, text });
  const tg = buildTelegramAskUrl({ appId });
  if (wa) {
    links.push({ id: 'whatsapp', label: 'WhatsApp', href: wa });
  }
  if (tg) {
    links.push({ id: 'telegram', label: 'Telegram', href: tg });
  }
  return links;
};
